import apiService from './api';
import type { ApiResponse } from '../types/api';
import type { Numeric } from '@/utils/numeric';

export interface WeatherObservation {
  id?: number;
  observed_at: string;
  temp_c: Numeric;
  temp_in_c?: Numeric;
  humidity: Numeric;
  humidity_in?: Numeric;
  dew_point_c: Numeric;
  feels_like_c: Numeric;
  wind_dir: number | null;
  wind_kph: Numeric;
  wind_gust_kph: Numeric;
  wind_kph_avg10m?: Numeric;
  max_daily_gust_kph?: Numeric;
  rain_mm_hour: Numeric;
  rain_mm_event?: Numeric;
  rain_mm_day: Numeric;
  rain_mm_week?: Numeric;
  rain_mm_month?: Numeric;
  rain_mm_total?: Numeric;
  solar_wm2: Numeric;
  uv: Numeric;
  pressure_hpa: Numeric;
  soil_temp_c_1?: Numeric;
  soil_moisture_1?: Numeric;
  leaf_wetness_1?: Numeric;
  pm25?: Numeric;
  co2?: Numeric;
  raw?: Record<string, unknown>;
}

export interface WeatherStation {
  id: number;
  organisation_id: number;
  farm_id: number | null;
  farm_name: string | null;
  mac_address: string;
  name: string;
  provider: string;
  lat: number | null;
  lng: number | null;
  elevation_m: Numeric;
  timezone: string | null;
  active: boolean;
  last_seen_at: string | null;
  last_poll_at: string | null;
  last_poll_error: string | null;
  created_at: string;
  updated_at: string;
  latest_observation?: WeatherObservation | null;
}

export interface WeatherStationObservationsResponse {
  observations: WeatherObservation[];
  summary: {
    rain_24h_mm: number | null;
    rain_7d_mm: number | null;
    rain_30d_mm: number | null;
    min_temp_7d: number | null;
    max_temp_7d: number | null;
    peak_gust_7d: number | null;
  };
  from: string;
  to: string;
}

export interface WeatherStationValidatePreview {
  mac_address: string;
  name: string | null;
  location: string | null;
  coords: { lat: number; lon: number } | null;
  elevation_m: Numeric;
  last_data_at: string | null;
}

export interface WeatherStationCreateInput {
  mac_address: string;
  name: string;
  application_key: string;
  api_key: string;
  farm_id?: number | null;
  lat?: number | null;
  lng?: number | null;
  elevation_m?: number | null;
}

class WeatherStationService {
  async list(): Promise<ApiResponse<WeatherStation[]>> {
    return apiService.get('/weather-stations');
  }

  async get(id: number): Promise<ApiResponse<WeatherStation>> {
    return apiService.get(`/weather-stations/${id}`);
  }

  async getObservations(
    id: number,
    from?: Date,
    to?: Date,
    limit = 2000,
  ): Promise<ApiResponse<WeatherStationObservationsResponse>> {
    const params = new URLSearchParams();
    if (from) params.set('from', from.toISOString());
    if (to) params.set('to', to.toISOString());
    params.set('limit', String(limit));
    return apiService.get(`/weather-stations/${id}/observations?${params.toString()}`);
  }

  async validateKey(
    applicationKey: string,
    apiKey: string,
  ): Promise<ApiResponse<WeatherStationValidatePreview[]>> {
    return apiService.post('/weather-stations/validate-key', {
      application_key: applicationKey,
      api_key: apiKey,
    });
  }

  async create(data: WeatherStationCreateInput): Promise<ApiResponse<WeatherStation>> {
    return apiService.post('/weather-stations', data);
  }

  async update(
    id: number,
    data: Partial<{
      name: string;
      farm_id: number | null;
      active: boolean;
      application_key: string;
      api_key: string;
    }>,
  ): Promise<ApiResponse<WeatherStation>> {
    return apiService.put(`/weather-stations/${id}`, data);
  }

  async triggerBackfill(id: number): Promise<ApiResponse<{ message: string }>> {
    return apiService.post(`/weather-stations/${id}/backfill`, {});
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/weather-stations/${id}`);
  }
}

export const weatherStationService = new WeatherStationService();
export default weatherStationService;
